/**
 * taskActions — knappene og «holdes i N t» som ChoreCard og HomeworkCard deler
 * (#154, steg 4b).
 *
 * Begge kortene står på TaskRow, der handlingen ligger i en spalte med fast
 * bredde 96 px. «Ta» er en `solid`-knapp og «Fullfør» en `primary`, begge 44 px
 * høye. Knappen bytter fra «Ta» via «Tar …» til «Fullfør» uten at raden endrer
 * form («Ta flytter aldri kortet», design/HANDOFF.md).
 *
 * Ingen optimistisk UI på «Ta» (CLAUDE.md, INV-CLAIM-2): `pending` er
 * `claim.isPending`, og den varer til serveren har svart og tavla er hentet på
 * nytt (useChoreActions). Først da viser kortet «Fullfør», fordi raden da er
 * tatt av deg i serverens data.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/primitives/Button';

const HOUR_MS = 3_600_000;

/**
 * Kantfargene (TaskRow `line`) som begge kortene bruker: lilla = din, som
 * skissens «Mine nå»-kort; amber = venter på en voksen.
 */
export const MINE_LINE = 'border-lilac/60';
export const AWAITING_LINE = 'border-warn-line';

/**
 * Timer igjen av claimet, rundet opp, minst 1. `null` når claimet ikke har noen
 * frist eller fristen er passert — da står bare «Din nå».
 */
export function claimHoursLeft(claimExpiresAt: string | null | undefined, now: Date): number | null {
  if (!claimExpiresAt) return null;
  const left = new Date(claimExpiresAt).getTime() - now.getTime();
  if (!Number.isFinite(left) || left <= 0) return null;
  return Math.max(1, Math.ceil(left / HOUR_MS));
}

/**
 * «Din nå · holdes i 24 t» på et kort du har tatt (`claim_expires_at`).
 * Teller ned hvert minutt, så kjøkkenskjermen ikke står med et gammelt tall.
 */
export function HeldByMeMeta({ claimExpiresAt }: { claimExpiresAt: string | null }): ReactNode {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!claimExpiresAt) return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [claimExpiresAt]);
  const hours = claimHoursLeft(claimExpiresAt, now);
  return (
    <span className="font-semibold text-lilac">
      {hours === null
        ? t('chores.yoursNow')
        : `${t('chores.yoursNow')} · ${t('chores.heldFor', { hours })}`}
    </span>
  );
}

interface TakeButtonProps {
  /** `chore-card` eller `homework-card`; gir `-claim` og `-spinner`. */
  testIdPrefix: string;
  locked: boolean;
  pending: boolean;
  onClaim?: (() => void) | undefined;
}

export function TakeButton({ testIdPrefix, locked, pending, onClaim }: TakeButtonProps): ReactNode {
  const { t } = useTranslation();
  // Mens serveren svarer kan knappen ikke trykkes igjen, men den står like
  // bred og like synlig (skissens «Tar …» med 0.75 opasitet).
  const inert = locked || pending;
  return (
    <Button
      variant="solid"
      size={44}
      data-testid={`${testIdPrefix}-claim`}
      data-pending={pending ? 'true' : 'false'}
      aria-disabled={inert ? 'true' : 'false'}
      onClick={inert ? undefined : onClaim}
      className={[
        locked ? 'opacity-50 pointer-events-none' : '',
        pending ? 'opacity-75' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {pending ? (
        <>
          <span
            data-testid={`${testIdPrefix}-spinner`}
            aria-hidden
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
          />
          {t('chores.action.taking')}
        </>
      ) : (
        t('chores.action.take')
      )}
    </Button>
  );
}

interface CompleteButtonProps {
  testIdPrefix: string;
  pending: boolean;
  onComplete?: (() => void) | undefined;
}

export function CompleteButton({ testIdPrefix, pending, onComplete }: CompleteButtonProps): ReactNode {
  const { t } = useTranslation();
  return (
    <Button
      variant="primary"
      size={44}
      data-testid={`${testIdPrefix}-complete`}
      data-pending={pending ? 'true' : 'false'}
      onClick={onComplete}
    >
      {t('chores.action.complete')}
    </Button>
  );
}

/** Delene i metalinja, skilt med « · ». Tomme deler hoppes over. */
export function joinMeta(parts: ReadonlyArray<ReactNode>): ReactNode {
  const present = parts.filter((p) => p !== null && p !== undefined && p !== false && p !== '');
  return present.map((p, i) => (
    // Rekkefølgen er fast per kort, så indeksen er en stabil nøkkel her.
    <span key={i}>
      {i > 0 ? ' · ' : null}
      {p}
    </span>
  ));
}
